import { onAuthStateChanged } from 'firebase/auth'
import { get, map, pick, uniq } from 'lodash'
import {
  type GetStaticPaths,
  type GetStaticProps,
  type NextPageWithLayout,
} from 'next'
import { useRouter } from 'next/router'
import React, { type ReactNode, useEffect } from 'react'
import { getPublicLayout } from '@public/components/PublicLayout'
import { Error } from 'components/Error'
import { LinkWithText } from 'components/Link'
import { Seo } from 'components/Seo'
import { Text } from 'components/Typography'
import { PAGE_MIN_HEIGHT, WrappedLoader } from 'components/WrappedLoader'
import { PRODUCT_VARIANT_STATUSES } from 'constants/common'
import { ROUTE } from 'constants/routes'
import { withGA4Provider } from 'hoc/withGoogleAnalytics'
import { useCart } from 'providers/cart'
import {
  GA4_AREAS,
  GA4_ITEM_LIST_NAME,
  GA4_PAGE_AREAS,
} from 'providers/googleAnalytics'
import { useUserAuth } from 'providers/userAuth'
import { FONT_SIZE } from 'Theme'
import {
  type ProductVariantStatus,
  usePublicProductQuery,
} from 'types/graphql-generated'
import auth from 'utils/firebase'
import {
  formatProductDetailPageDescription,
  formatProductDetailPageMetaTitle,
} from 'utils/formatters'
import { formatProductDetailTitle } from 'utils/formatTitle'
import { getProductRoute } from 'utils/getProductRoute'
import { klaviyoTrackViewedShowroomProduct } from 'utils/klaviyo'
import { pushUrl } from 'utils/pushUrl'
import { ProductDetailPage } from './ProductDetailPage'
import {
  getProductIdFromSlug,
  getVariantIdFromSlug,
  sortProductVariantsByPriceAndQuantity,
  getProductVariantSlug,
} from './utils'
import { prepareGetStaticProps } from './utils/prepareGetStaticProps'

interface ProductDetailProps {
  id?: number
  isPreview?: boolean
}

const ProductDetailContent: NextPageWithLayout<ProductDetailProps> = ({
  id,
  isPreview,
}) => {
  const router = useRouter()
  const { isAdminSignedIn, isPageLoading } = useUserAuth()
  const { isCartLoading } = useCart()
  const { query } = router
  const productId = id ?? getProductIdFromSlug(query.productSlug as string)
  const {
    data,
    loading: isProductQueryLoading,
    refetch,
  } = usePublicProductQuery({
    fetchPolicy: 'cache-first',
    skip: !productId || (isPreview && !isAdminSignedIn),
    variables: {
      productInput: {
        id: productId,
      },
      variantStatuses: [
        PRODUCT_VARIANT_STATUSES.PUBLISHED,
        PRODUCT_VARIANT_STATUSES.NOT_PUBLISHED,
        PRODUCT_VARIANT_STATUSES.ARCHIVED,
        ...(isPreview ? [PRODUCT_VARIANT_STATUSES.PENDING_APPROVAL] : []),
      ] as ProductVariantStatus[],
    },
  })

  // Trigger refetch when auth state changes to make sure that trade user sees correct prices rendered on the page
  useEffect(() => {
    const cleanup = onAuthStateChanged(auth, async () => {
      await refetch()
    })
    return cleanup
  }, [refetch])

  const isLoading = isProductQueryLoading || isCartLoading
  const product = get(data, 'product')
  const sortedVariants = sortProductVariantsByPriceAndQuantity(
    get(product, 'variants', [])
  )

  const selectedVariant =
    sortedVariants.find(
      ({ id: variantId }) =>
        variantId === getVariantIdFromSlug(query.variantSlug)
    ) || get(sortedVariants, 0)

  useEffect(() => {
    if (product && selectedVariant) {
      if (product.forceRedirectToProduct) {
        const route = ROUTE.SHOWROOM_PRODUCT_DETAIL({
          slug: product.forceRedirectToProduct.slug,
        })
        pushUrl({ router, route, params: pick(query, 'type') })
      }

      if (
        !query.variantSlug ||
        query.productSlug !== product.slug ||
        query.variantSlug !== selectedVariant.slug
      ) {
        // Handle the case when we are not on products/slug
        if (!product?.slug) {
          return
        }

        const route = getProductRoute({
          expertSlug: query.slug as string,
          slug: product.slug,
          variantSlug: getProductVariantSlug(selectedVariant),
          isPreview,
        })
        pushUrl({ router, route, params: pick(query, 'type') })
      }
      if (!isPreview) {
        klaviyoTrackViewedShowroomProduct({
          productId: product.id,
          productVariantId: selectedVariant.id,
          productVariantSku: selectedVariant.sku,
        })
      }
    }
  }, [isPreview, product, query, router, selectedVariant])

  if (!product || !selectedVariant) {
    return (
      <Error
        description={
          <Text fontSize={FONT_SIZE.PX_16}>
            Try searching again or choose products in{' '}
            <LinkWithText
              fontSize={FONT_SIZE.PX_16}
              href={ROUTE.SHOWROOM_PRODUCTS()}
              aria-label="the list of showroom products"
            >
              the list
            </LinkWithText>
          </Text>
        }
      >
        We couldn&apos;t find the product you&apos;re looking for
      </Error>
    )
  }

  const areVariantTitlesUnique =
    uniq(map(product.variants, 'title')).length === product.variants.length

  const seoDescription =
    selectedVariant?.customContent?.accordionDetails ||
    product.customContent?.accordionDetails ||
    product.description

  // Check also if page is loading to avoid flashing content for trade users when trade prices aren't available yet
  if ((!id && isLoading) || isPageLoading) {
    return (
      <>
        <Seo
          title={formatProductDetailTitle(
            selectedVariant.title ?? product.title,
            areVariantTitlesUnique
              ? []
              : selectedVariant.optionValues.map(({ value }) => value)
          )}
          metaTitle={formatProductDetailPageMetaTitle({
            title: selectedVariant.title,
            brand: product.brand?.id,
            optionValues: selectedVariant.optionValues,
          })}
          image={
            selectedVariant.images?.length
              ? selectedVariant.images[0]?.url
              : product.images[0]?.url
          }
          urlPath={ROUTE.SHOWROOM_PRODUCT_DETAIL({
            slug: product.slug,
            variantSlug: selectedVariant.slug,
          })}
          publisher={product?.brand?.id}
          description={formatProductDetailPageDescription({
            title: selectedVariant.title,
            description: seoDescription,
            brand: product.brand?.id,
            optionValues: selectedVariant.optionValues,
          })}
          canonicalUrl={ROUTE.SHOWROOM_PRODUCT_DETAIL({
            slug: product.slug,
            variantSlug: selectedVariant.slug,
          })}
        />
        <WrappedLoader minHeight={PAGE_MIN_HEIGHT} />
      </>
    )
  }

  return (
    <ProductDetailPage
      productId={productId}
      product={product}
      isPreview={isPreview}
      isLoading={isLoading}
      selectedVariant={selectedVariant}
      sortedVariants={sortedVariants}
    />
  )
}

export const ProductDetail = withGA4Provider({
  area: GA4_AREAS.SHOWROOM,
  page_area: GA4_PAGE_AREAS.PDP,
  item_list_name: GA4_ITEM_LIST_NAME.PRODUCT_DETAIL_PAGE,
})(ProductDetailContent) as NextPageWithLayout<ProductDetailProps>

export const getStaticPaths: GetStaticPaths = () => ({
  paths: [],
  fallback: 'blocking',
})

export const getStaticProps: GetStaticProps = (context) =>
  prepareGetStaticProps(context, {
    slug: context.params.slug,
    productSlug: context.params.productSlug,
    variantSlug: context.params.variantSlug,
  })

const PageLayout = (page: ReactNode) =>
  getPublicLayout(page, { isFullWidth: true, isWithBreadcrumbs: true })

ProductDetail.getLayout = PageLayout
